/**
 * ToolSEOContent - Adds 500+ word SEO landing content to tool pages
 * Improves organic ranking by providing unique, keyword-rich content
 * Includes: description, features, how-to, FAQ, and CTA
 */
import { useState } from "react";
import { ChevronDown, ChevronUp, CheckCircle } from "lucide-react";
import InternalLinks from "./InternalLinks";

const TOOL_CONTENT = {
  "hashtag-generator": {
    title: "Free Hashtag Generator for Instagram, TikTok, Twitter & LinkedIn",
    intro: "Finding the right hashtags can make or break your social media reach. Our free hashtag generator analyzes trending topics, competitor strategies, and engagement data to suggest the most effective hashtags for your content. Whether you're growing an Instagram page, building a TikTok audience, or expanding your LinkedIn presence, the right hashtags connect your content with people who are actively searching for it.",
    features: [
      "AI-powered hashtag suggestions based on your topic or keyword",
      "Platform-specific hashtags for Instagram, TikTok, Twitter, and LinkedIn",
      "Mix of high-volume and niche hashtags for optimal reach",
      "One-click copy to clipboard for instant use",
      "Trending hashtag discovery updated in real-time",
      "Hashtag analytics showing estimated reach and competition"
    ],
    howTo: [
      "Enter your topic, keyword, or niche in the search box",
      "Select your target platform (Instagram, TikTok, Twitter, or LinkedIn)",
      "Click 'Generate Hashtags' to get AI-curated suggestions",
      "Review the hashtag sets organized by reach and relevance",
      "Copy your preferred set and paste directly into your post"
    ],
    faqs: [
      { q: "How many hashtags should I use on Instagram?", a: "Instagram allows up to 30 hashtags per post, but studies show 8-15 highly relevant hashtags perform best. Our generator provides a balanced mix of popular and niche hashtags to maximize visibility without looking spammy." },
      { q: "Do hashtags work on TikTok?", a: "Yes, hashtags are crucial for TikTok discovery. TikTok's algorithm uses hashtags to categorize content and show it to interested users. Use 3-5 targeted hashtags per TikTok video for best results." },
      { q: "Is this hashtag generator really free?", a: "Absolutely! Our hashtag generator is 100% free with no signup required. You get unlimited hashtag generations, copy-paste functionality, and platform-specific suggestions at no cost." },
      { q: "How often should I change my hashtags?", a: "Rotate your hashtags every 2-3 posts. Using the same hashtags repeatedly can reduce your reach. Our tool generates fresh combinations each time to keep your strategy dynamic." }
    ]
  },
  "keyword-research": {
    title: "Free Keyword Research Tool - Find High-Traffic, Low-Competition Keywords",
    intro: "Keyword research is the foundation of every successful SEO strategy. Our free keyword research tool helps you discover what people are actually searching for, how competitive those terms are, and which keywords give you the best chance of ranking. Unlike expensive SEO suites that charge hundreds per month, MarkeeMix gives you essential keyword data completely free — search volume estimates, keyword difficulty scores, related keywords, and People Also Ask questions.",
    features: [
      "Search volume estimates for any keyword",
      "Keyword difficulty score to assess competition",
      "Related keywords and long-tail variations",
      "People Also Ask (PAA) questions from Google",
      "SERP analysis showing top-ranking pages",
      "Keyword grouping and clustering suggestions"
    ],
    howTo: [
      "Enter a seed keyword related to your topic or business",
      "Click 'Research Keyword' to analyze the search landscape",
      "Review search volume, difficulty, and competition data",
      "Explore related keywords and long-tail opportunities",
      "Export your keyword list and start creating optimized content"
    ],
    faqs: [
      { q: "What is keyword research?", a: "Keyword research is the process of finding words and phrases that people type into search engines. By targeting the right keywords in your content, you can attract more organic traffic from Google and other search engines." },
      { q: "How accurate is the search volume data?", a: "Our tool provides estimated search volumes based on multiple data sources. While no tool provides exact numbers (even Google's Keyword Planner shows ranges), our estimates give you a reliable relative comparison between keywords." },
      { q: "How do I find easy keywords to rank for?", a: "Look for keywords with decent search volume (100-1,000/month) but low difficulty scores (under 30). Long-tail keywords (3-5 words) are typically easier to rank for and often have higher conversion rates." },
      { q: "Can I use this for YouTube SEO?", a: "Yes! Keywords research applies to YouTube too. Search for topics relevant to your channel and use the keyword suggestions in your video titles, descriptions, and tags." }
    ]
  },
  "seo-audit": {
    title: "Free SEO Audit Tool - Check Your Website's Health Score Instantly",
    intro: "Is your website losing traffic because of hidden SEO issues? Our free SEO audit tool scans your website and identifies technical problems, on-page SEO gaps, security vulnerabilities, and performance bottlenecks that are holding your rankings back. Get a detailed health score across 100+ checkpoints covering SEO, performance, security, mobile-friendliness, and technical factors. Most SEO audit tools charge premium prices for this level of analysis — we give it to you completely free.",
    features: [
      "100+ SEO checkpoints analyzed in seconds",
      "Overall health score with category breakdown",
      "On-page SEO analysis (title, meta, headings, content)",
      "Technical SEO audit (canonical, robots, schema, sitemap)",
      "Performance metrics including Core Web Vitals",
      "Security assessment (SSL, headers, mixed content)",
      "Mobile-friendliness and responsive design check",
      "Actionable recommendations sorted by priority"
    ],
    howTo: [
      "Enter your website URL in the audit field",
      "Click 'Run Full Audit' and wait 10-15 seconds",
      "Review your overall health score and category scores",
      "Check the 'Quick Wins' section for immediate improvements",
      "Work through high-priority issues first, then medium and low"
    ],
    faqs: [
      { q: "How often should I audit my website?", a: "Run a full SEO audit at least once a month, and after any major website changes. Regular audits catch issues before they impact your rankings." },
      { q: "What is a good SEO score?", a: "A score above 80 is good, above 90 is excellent. Most websites score between 40-70 on their first audit. Focus on fixing high-priority issues first for the biggest score improvement." },
      { q: "Will this audit affect my website?", a: "No. Our audit tool only reads your website's public HTML — it doesn't modify anything. It's completely safe and non-intrusive." },
      { q: "Can I audit competitor websites?", a: "Yes! You can audit any publicly accessible website. This is great for competitor analysis — see what they're doing well and where they're falling short." }
    ]
  },
  "backlink-checker": {
    title: "Free Backlink Checker - Analyze Any Website's Link Profile",
    intro: "Backlinks remain one of Google's top ranking factors. Our free backlink checker lets you analyze any website's link profile — see who's linking to them, assess link quality, and discover link-building opportunities for your own site. Understanding your backlink profile is essential for SEO success, and checking competitor backlinks reveals strategies you can replicate.",
    features: [
      "Complete backlink profile analysis",
      "Domain authority and page authority scores",
      "Referring domains count and quality assessment",
      "Dofollow vs nofollow link breakdown",
      "Anchor text distribution analysis",
      "New and lost backlinks tracking"
    ],
    howTo: [
      "Enter any website URL you want to analyze",
      "Click 'Check Backlinks' to scan the link profile",
      "Review the domain authority score and total backlinks",
      "Examine referring domains for link quality",
      "Use competitor insights to plan your link building strategy"
    ],
    faqs: [
      { q: "What are backlinks?", a: "Backlinks are links from other websites pointing to yours. They act as 'votes of confidence' that tell search engines your content is trustworthy and valuable. More quality backlinks generally means higher rankings." },
      { q: "How many backlinks do I need to rank?", a: "Quality matters more than quantity. One backlink from a high-authority site (like a news outlet) can be worth more than hundreds from low-quality directories. Focus on earning relevant, authoritative links." },
      { q: "Can I check my competitor's backlinks?", a: "Yes! Enter any competitor URL to see their complete link profile. This reveals their link-building strategy and helps you find websites that might link to you too." }
    ]
  },
  "site-audit": {
    title: "Complete Website Audit - SEO, Performance, Security & Mobile Analysis",
    intro: "A comprehensive website audit is the first step to improving your search rankings. MarkeeMix's site audit tool crawls your website and analyzes over 100 factors across SEO, performance, security, and mobile-friendliness. Unlike basic checkers that only scan your homepage, our advanced audit examines multiple pages, checks Core Web Vitals, validates structured data, and provides prioritized recommendations with estimated impact scores.",
    features: [
      "Multi-page crawl and analysis",
      "Core Web Vitals measurement (LCP, FID, CLS)",
      "Structured data validation",
      "Security header assessment",
      "Mobile responsiveness scoring",
      "Internal link structure analysis",
      "Image optimization check",
      "Page speed and load time analysis"
    ],
    howTo: [
      "Navigate to the Site Audit tool from the SEO Tools menu",
      "Enter your website URL and click 'Run Full Audit'",
      "Wait while our crawler analyzes your pages (10-30 seconds)",
      "Review scores across SEO, Performance, Security, Mobile, and Technical",
      "Start with 'Quick Wins' for immediate impact improvements"
    ],
    faqs: [
      { q: "What's the difference between Site Audit and SEO Audit?", a: "Site Audit is our comprehensive multi-page analysis tool that checks SEO, performance, security, and mobile factors. SEO Audit is a lighter single-page check focused specifically on search engine optimization factors." },
      { q: "How long does a full audit take?", a: "Most audits complete in 10-30 seconds depending on the website size. We analyze the homepage and up to 10 internal pages for a thorough assessment." },
      { q: "What should I fix first after an audit?", a: "Start with items marked as 'Critical' or 'High Priority' — these have the biggest impact on your rankings. Common quick wins include fixing missing title tags, adding meta descriptions, and resolving broken links." }
    ]
  },
  "content-tools": {
    title: "AI Content Marketing Tools - Blog Writer, Article Rewriter & Optimizer",
    intro: "Creating high-quality content consistently is the biggest challenge for marketers and bloggers. MarkeeMix's AI-powered content tools help you write, rewrite, and optimize content faster without sacrificing quality. From generating complete blog posts to rewriting existing articles for uniqueness, our suite of content marketing tools uses advanced AI to save you hours of writing time while maintaining your brand voice.",
    features: [
      "AI blog post generator with SEO optimization",
      "Article rewriter that maintains original meaning",
      "Headline and title generator for higher CTR",
      "Readability checker with improvement suggestions",
      "Content optimizer for target keywords",
      "Plagiarism detection and uniqueness scoring"
    ],
    howTo: [
      "Choose your content tool (Writer, Rewriter, or Optimizer)",
      "Enter your topic, keyword, or existing content",
      "Select output parameters (tone, length, style)",
      "Generate content and review the AI output",
      "Edit, refine, and publish your optimized content"
    ],
    faqs: [
      { q: "Will AI content rank on Google?", a: "Google's stance is that quality matters, not whether content is AI-generated. Our tools are designed to create helpful, accurate content that provides real value to readers. Always review and add your expertise before publishing." },
      { q: "Can I rewrite content to make it unique?", a: "Yes! Our article rewriter paraphrases content while preserving the original meaning. This is useful for creating unique versions of your content for different platforms or updating old articles." },
      { q: "What reading level should my content target?", a: "For most web content, aim for a 6th-8th grade reading level. Our readability checker scores your content and suggests simplification where needed for maximum accessibility." }
    ]
  },
  "youtube-tools": {
    title: "YouTube SEO Tools - Optimize Videos for More Views & Subscribers",
    intro: "YouTube is the world's second-largest search engine, and YouTube SEO determines whether your videos get discovered or buried. MarkeeMix's YouTube toolkit helps creators and businesses optimize every aspect of their videos — from titles and tags to thumbnails and upload timing. Whether you're a new creator or managing a channel with thousands of subscribers, the right optimization can multiply your views without spending a rupee on ads.",
    features: [
      "Video title optimizer with click-through rate predictions",
      "Tag generator based on trending and relevant keywords",
      "Thumbnail analyzer and best practices checker",
      "Best upload time finder based on audience activity",
      "Channel analytics and competitor comparison",
      "Video description template generator"
    ],
    howTo: [
      "Select the YouTube tool you need from the sidebar",
      "Enter your video topic or paste your video URL",
      "Review optimization suggestions and scoring",
      "Apply recommended titles, tags, and descriptions",
      "Track performance improvements in your YouTube Studio"
    ],
    faqs: [
      { q: "How do YouTube tags help with SEO?", a: "YouTube tags help the algorithm understand your video's topic and suggest it to relevant viewers. Use a mix of broad and specific tags. Our tag generator finds the best combinations based on search volume and competition." },
      { q: "What makes a good YouTube title?", a: "A good title is under 60 characters, includes your target keyword, and creates curiosity. Numbers, brackets, and emotional words increase click-through rates. Our title optimizer scores these factors automatically." },
      { q: "When is the best time to upload on YouTube?", a: "It depends on your audience's timezone and habits. Generally, weekday afternoons (2-4 PM) and weekends (9-11 AM) perform well in India. Our upload time finder analyzes your specific audience patterns." }
    ]
  },
  "social-media-tools": {
    title: "Social Media Marketing Tools - Hashtags, Scheduling & Analytics",
    intro: "Managing social media effectively requires the right tools. MarkeeMix's social media suite covers everything from generating trending hashtags to scheduling posts and analyzing engagement metrics. Built specifically for creators, small businesses, and digital marketers who need professional-grade tools without the enterprise price tag. Grow your Instagram, TikTok, Twitter, LinkedIn, and Facebook presence with data-driven strategies.",
    features: [
      "Hashtag generator for all major platforms",
      "Post scheduling and content calendar",
      "Instagram analytics and follower insights",
      "Engagement rate calculator",
      "Social media caption generator",
      "Competitor social media analysis"
    ],
    howTo: [
      "Choose your social media tool from the sidebar",
      "Enter your content topic or paste your post",
      "Select your target platform for optimized suggestions",
      "Apply hashtags, captions, or scheduling recommendations",
      "Track engagement and adjust your strategy weekly"
    ],
    faqs: [
      { q: "Which social media platform should I focus on?", a: "Focus on where your audience is. Instagram and YouTube dominate in India for visual content. LinkedIn works best for B2B. TikTok is growing fast for short-form content. Start with 1-2 platforms and expand." },
      { q: "How often should I post on social media?", a: "Consistency matters more than frequency. For Instagram: 3-5 posts/week. Twitter: 1-3 tweets/day. LinkedIn: 2-3 posts/week. TikTok: 1-3 videos/day. Quality always beats quantity." },
      { q: "Can I schedule posts for multiple platforms?", a: "Yes! Our social media scheduler lets you plan and queue posts across platforms. Set your posting calendar once and let it auto-publish at optimal times." }
    ]
  },
  "pdf-tools": {
    title: "Free PDF Tools Online - Convert, Merge, Split, Compress & Edit PDFs",
    intro: "Need to quickly merge PDFs, convert images to PDF, or compress a large document? MarkeeMix offers 40+ free PDF tools that work entirely in your browser — no software installation needed. Upload your files, make your changes, and download the result in seconds. All processing happens securely, and your files are automatically deleted after processing. Perfect for students, professionals, and businesses who need fast, reliable PDF operations.",
    features: [
      "Merge multiple PDFs into one document",
      "Split PDFs into separate pages or sections",
      "Convert JPG, PNG, Word, Excel to PDF and back",
      "Compress PDFs to reduce file size",
      "Add watermarks, page numbers, and stamps",
      "Password protect and encrypt PDF files",
      "OCR text extraction from scanned documents",
      "Edit text and images directly in PDFs"
    ],
    howTo: [
      "Select the PDF operation you need",
      "Upload your PDF file(s) by drag-and-drop or file picker",
      "Configure options (page range, quality, format)",
      "Click 'Process' and wait a few seconds",
      "Download your processed PDF file"
    ],
    faqs: [
      { q: "Are my PDF files safe?", a: "Yes. All files are processed in your browser or our secure servers. Files are automatically deleted after processing. We never store, read, or share your documents." },
      { q: "Is there a file size limit?", a: "Free users can process PDFs up to 25MB. Pro users get unlimited file sizes. For most documents, the free tier is more than sufficient." },
      { q: "Can I convert PDF to Word?", a: "Yes! Our PDF to Word converter maintains formatting, tables, and images. It works best with text-based PDFs. Scanned PDFs use OCR technology for text extraction." }
    ]
  },
  "ai-blog": {
    title: "AI Blog Generator - Create SEO-Optimized Articles in Minutes",
    intro: "Writing a blog post that ranks on Google typically takes 4-8 hours of research, writing, and optimization. MarkeeMix's AI Blog Generator cuts that to minutes. Enter your topic, select your target keywords, and our AI creates a comprehensive, SEO-optimized article with proper heading structure, keyword placement, internal links, and meta tags. Perfect for content marketers, agency teams, and bloggers who need to scale their content production without sacrificing quality.",
    features: [
      "Complete blog post generation from a single topic",
      "SEO-optimized heading structure (H1, H2, H3)",
      "Natural keyword integration throughout the article",
      "Customizable tone, length, and writing style",
      "Auto-generated meta title and description",
      "FAQ section generation for featured snippets"
    ],
    howTo: [
      "Enter your blog topic or target keyword",
      "Select article length, tone, and style preferences",
      "Click 'Generate' and wait 30-60 seconds",
      "Review the AI-generated article with headings and paragraphs",
      "Edit, add your expertise, and publish"
    ],
    faqs: [
      { q: "How long are the generated articles?", a: "You can choose article length from 500 to 3,000+ words. Longer articles (1,500-2,500 words) tend to rank better for competitive keywords. Our AI maintains quality regardless of length." },
      { q: "Do I need to edit the AI content?", a: "We recommend reviewing and adding your personal expertise to AI-generated content. Add real examples, case studies, and opinions that only you can provide. This combination of AI efficiency and human expertise produces the best results." },
      { q: "Can AI-generated blogs rank on Google?", a: "Yes, if the content is helpful, accurate, and provides value. Google evaluates content quality, not authorship method. Always fact-check AI output and add unique insights before publishing." }
    ]
  },
  "plagiarism-checker": {
    title: "Free Plagiarism Checker - Verify Content Originality Instantly",
    intro: "Publishing duplicate content damages your SEO rankings and credibility. Our free plagiarism checker scans your text against billions of web pages to identify copied content, paraphrased sections, and potential originality issues. Essential for bloggers, students, content agencies, and anyone who needs to verify that their content is 100% unique before publishing.",
    features: [
      "Deep scan against billions of web pages",
      "Percentage-based originality scoring",
      "Highlighted duplicate passages with source URLs",
      "Paraphrase detection for spun content",
      "Batch checking for multiple documents",
      "Detailed report with improvement suggestions"
    ],
    howTo: [
      "Paste your text into the checker or upload a document",
      "Click 'Check Plagiarism' to start the scan",
      "Review the originality score and highlighted sections",
      "Click on flagged passages to see matching sources",
      "Rewrite flagged sections and re-check until score is 95%+"
    ],
    faqs: [
      { q: "What plagiarism score is acceptable?", a: "Aim for 95% or higher originality. Some common phrases naturally match existing content, so 100% is rare. If your score is below 85%, review and rewrite the flagged sections." },
      { q: "Can it detect paraphrased content?", a: "Yes, our advanced algorithm detects both direct copies and paraphrased content where the meaning is preserved but words are changed. This catches sophisticated content spinning." },
      { q: "Is there a word limit for free users?", a: "Free users can check up to 1,000 words per scan. Pro users get unlimited word count per check. Most blog posts and articles fit within the free limit." }
    ]
  },
  "domain-authority": {
    title: "Free Domain Authority Checker - Check DA Score of Any Website",
    intro: "Domain Authority (DA) is a search engine ranking score that predicts how likely a website is to rank in search results. Our free domain authority checker gives you instant DA scores along with page authority, spam score, and backlink metrics for any website. Use it to evaluate your own site's SEO strength, assess potential link-building targets, or analyze competitor domains.",
    features: [
      "Instant Domain Authority (DA) score lookup",
      "Page Authority (PA) for specific URLs",
      "Spam score assessment",
      "Total backlinks and referring domains count",
      "Historical DA tracking over time",
      "Bulk domain checking capability"
    ],
    howTo: [
      "Enter a domain name (e.g., example.com)",
      "Click 'Check Authority' for instant results",
      "Review DA score, PA score, and spam metrics",
      "Compare with competitor domains",
      "Track your DA improvement over time"
    ],
    faqs: [
      { q: "What is a good Domain Authority score?", a: "DA 40-50 is average, 50-60 is good, and 60+ is excellent. New websites typically start at DA 1-10. Focus on quality content and link building to improve your DA over time." },
      { q: "How is Domain Authority calculated?", a: "DA is calculated based on multiple factors including total backlinks, referring domains, link quality, and website age. It's scored on a logarithmic scale of 1-100, meaning going from 20 to 30 is easier than 70 to 80." },
      { q: "Can I increase my Domain Authority?", a: "Yes! Earn quality backlinks from authoritative websites, create valuable content, fix technical SEO issues, and remove toxic backlinks. DA improvement is gradual — expect 5-10 points improvement over 6 months with consistent effort." }
    ]
  },
  "design-tools": {
    title: "Free Design Tools - Image Editor, Color Palette Generator & Brand Kit",
    intro: "Professional design doesn't require expensive software. MarkeeMix's free design tools help marketers, creators, and small businesses create stunning visuals, generate brand-consistent color palettes, and build complete brand kits — all from your browser. No design skills required. Whether you need social media graphics, marketing banners, or a complete brand identity, our tools make professional design accessible to everyone.",
    features: [
      "Browser-based image editor with layers and filters",
      "Color palette generator from images or themes",
      "Brand kit creator with logo, fonts, and colors",
      "Social media template library",
      "Image resizer for all platform dimensions",
      "Background remover and image optimizer"
    ],
    howTo: [
      "Select your design tool from the sidebar",
      "Upload an image or start from a template",
      "Use the editor to customize colors, text, and layout",
      "Preview your design for different platforms",
      "Download in your preferred format (PNG, JPG, WebP)"
    ],
    faqs: [
      { q: "Do I need design experience to use these tools?", a: "Not at all! Our tools are designed for non-designers. Templates, AI suggestions, and intuitive controls make it easy to create professional-looking designs in minutes." },
      { q: "What file formats are supported?", a: "You can upload PNG, JPG, WebP, and SVG files. Downloads are available in PNG, JPG, and WebP formats. Our tools automatically optimize images for web use." },
      { q: "Can I create a complete brand kit?", a: "Yes! Our brand kit generator creates a comprehensive identity including logo suggestions, primary and secondary colors, typography recommendations, and usage guidelines." }
    ]
  },
  "all-tools": {
    title: "75+ Free Digital Marketing Tools - SEO, Content, Social Media & More",
    intro: "MarkeeMix brings together 75+ AI tools in one platform so you never need to juggle multiple subscriptions again. From SEO analysis and keyword research to AI content generation, social media management, PDF editing, and Google Ads optimization — every tool a digital marketer needs is right here. Most tools work instantly with no signup required, and our free tier is genuinely useful, not just a teaser for paid features.",
    features: [
      "SEO Suite: Keyword research, backlink checker, site audit, rank tracker",
      "Content Tools: AI writer, rewriter, plagiarism checker, readability scorer",
      "YouTube SEO: Tag generator, title optimizer, thumbnail analyzer",
      "Social Media: Hashtag generator, scheduler, Instagram analytics",
      "PDF Tools: 40+ operations including merge, split, convert, compress",
      "Design: Image editor, color palette, brand kit generator",
      "Advertising: Google Ads copy generator (Claude Sonnet 4.5) + keyword planning",
      "AI Tools: Blog generator, image creator, content optimizer"
    ],
    howTo: [
      "Browse tools by category using the navigation menu",
      "Select any tool to start using it instantly",
      "No signup required for most free tools",
      "Create a free account to save your work and get more features",
      "Upgrade to Pro for unlimited access and advanced features"
    ],
    faqs: [
      { q: "Are these tools really free?", a: "Yes! Most tools work without signup and have generous free limits. Free users get 3-5 daily uses per tool. Pro users get unlimited access to everything." },
      { q: "How does MarkeeMix compare to Semrush or Ahrefs?", a: "MarkeeMix offers a broader range of tools (SEO + content + social + design + PDF) in one platform. While Semrush and Ahrefs have deeper enterprise data, MarkeeMix is ideal for freelancers, small businesses, and creators who need comprehensive tools at an affordable price." },
      { q: "Is my data safe on MarkeeMix?", a: "Your data is encrypted in transit and at rest. We use secure Google OAuth for authentication. Files uploaded to PDF tools are automatically deleted after processing. We never sell or share your data." }
    ]
  }
};

export default function ToolSEOContent({ toolId }) {
  const [openFaq, setOpenFaq] = useState(null);
  const content = TOOL_CONTENT[toolId];

  if (!content) return null;

  return (
    <section className="max-w-4xl mx-auto px-4 py-12" data-testid={`seo-content-${toolId}`}>
      {/* Main description */}
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{content.title}</h2>
      <p className="text-gray-600 leading-relaxed mb-8">{content.intro}</p>

      {/* Features */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Key Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {content.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
              <span className="text-sm text-gray-600">{f}</span>
            </div>
          ))}
        </div>
      </div>

      {/* How to use */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">How to Use</h3>
        <ol className="space-y-2">
          {content.howTo.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
              <span className="text-sm text-gray-600">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* FAQ */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Frequently Asked Questions</h3>
        <div className="space-y-2">
          {content.faqs.map((faq, i) => (
            <div key={i} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-sm text-gray-800">{faq.q}</span>
                {openFaq === i ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Internal Links for SEO */}
      <InternalLinks currentPath={window.location.pathname} />
    </section>
  );
}
